import { createAsyncThunk } from "@reduxjs/toolkit";
import { Timestamp, collection, doc } from "firebase/firestore";
import { ref, uploadBytesResumable } from "firebase/storage";
import { CaseDocumentsAPI } from "../../api/caseDocuments";
import { db, storage } from "../../api/firebaseApi";
import {
  createUniqueFileForUpload,
  filterDocumentsBySupport
} from "../../helpers/file_helpers";
import { DataDocs } from "../../types/tables-data";
import {
  addCorruptRemovedNames,
  addSubDocument,
  setIsUploadingDocuments
} from "../documents/documentSlice";
import { generateDocumentUrls } from "../documents/documentsThunk";
import { VisaDocumentType } from "../documents/types";
import { RootState } from "../store";
import {
  setFileError,
  setIsLoading,
  setUploadProgress
} from "./fileUploadSlice";

export type UploadFilesProps = {
  path: string;
  uid: string;
  mainDocumentId?: string;
  type?: VisaDocumentType;
};

type CreateDocumentData = {
  fileRef: any;
  isSub?: boolean;
  category?: string;
  type?: string;
  criterion?: string;
  super_class?: string;
  customType?: string | null;
};

const createDocumentData = (
  data: CreateDocumentData,
  isSub: boolean
): DataDocs => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { fileRef, category, type, super_class, customType } = data;
  const currentTime = Timestamp.now();

  return {
    filePath: data.fileRef.fullPath,
    docNames: fileRef.name,
    documentTitle: isSub ? fileRef.name : "",
    uploadBy: "",
    description: "",
    super_class,
    uploadDate: currentTime,
    isDeleted: false,
    ...(category ? { criterion: category } : {}),
    ...(type ? { type } : {}),
    ...(customType ? { customType } : {}),
    isNew: true
  };
};

export const uploadFiles = createAsyncThunk(
  "fileUpload/uploadFiles",
  async (_, { rejectWithValue, dispatch, getState }) => {
    const state = getState() as RootState;
    const { files, uploadConfiguration } = state.fileUpload;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { path, uid, mainDocument, super_class, category, type, customType } =
      uploadConfiguration;

    const corruptedFilesNames = files
      .filter((f) => f.size === 0)
      .map((f) => f.name);
    if (
      corruptedFilesNames.length > 0 &&
      uploadConfiguration.source !== "external-link"
    ) {
      dispatch(addCorruptRemovedNames(corruptedFilesNames));
    }
    const filteredFiles = filterDocumentsBySupport(
      files.filter((f) => f.size > 0),
      true
    );

    // Mirror the staged-evidence batch flow: when uploading several top-level
    // documents at once, create them all in one API call so they are submitted
    // to Extend as a single parse batch instead of one batch run per file.
    // Bundle/sub-document uploads and single-file uploads keep the per-file path.
    const shouldBatch = filteredFiles.length > 1 && mainDocument === undefined;

    try {
      dispatch(setIsUploadingDocuments(true));
      const currentProgress = new Map<string, number>();
      const failedFileNames: string[] = [];
      const uploadPromises = filteredFiles.map(async (file) => {
        const { file: uniqueFile, path: uniquePath } =
          await createUniqueFileForUpload(path, file);
        return new Promise((resolve, reject) => {
          const fileRef = ref(storage, uniquePath);
          const uploadTask = uploadBytesResumable(fileRef, uniqueFile);

          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              currentProgress.set(file.name, progress);
              const progressObject = Object.fromEntries(currentProgress);
              dispatch(setUploadProgress(progressObject));
            },
            (error) => {
              failedFileNames.push(file.name);
              if (uploadConfiguration?.source !== "external-link") {
                dispatch(addCorruptRemovedNames([file.name]));
              }
              const errorMessage = error?.message || "Upload failed";
              dispatch(
                setFileError({ fileName: file.name, error: errorMessage })
              );
              resolve(null);
            },
            async () => {
              const documentData: CreateDocumentData = {
                fileRef,
                ...(category !== undefined && { category }),
                ...(type !== undefined && { type }),
                criterion: category ?? "",
                super_class: super_class || "",
                ...(customType !== undefined && { customType })
              };

              // Call createDocumentData with the documentData object
              const document = createDocumentData(
                documentData,
                mainDocument !== undefined
              );

              try {
                // Pre-generate a valid document ID
                const tempDocRef = doc(collection(db, "temp"));
                const documentId = tempDocRef.id;

                const baseDocumentData = {
                  individual_id: uid,
                  document_id: documentId,
                  filePath: document.filePath ?? fileRef.fullPath,
                  docNames: document.docNames ?? fileRef.name,
                  documentTitle:
                    mainDocument !== undefined
                      ? document.documentTitle ?? fileRef.name
                      : document.documentTitle || undefined,
                  uploadBy: document.uploadBy,
                  description: document.description,
                  super_class: document.super_class,
                  criterion: document.criterion ?? undefined,
                  type: document.type,
                  uploadDate:
                    document.uploadDate instanceof Timestamp
                      ? document.uploadDate.toDate().toISOString()
                      : typeof document.uploadDate === "number"
                      ? new Date(document.uploadDate as number).toISOString()
                      : (document.uploadDate as string | undefined),
                  isDeleted: document.isDeleted,
                  isNew: document.isNew ?? true
                };

                // In batch mode, defer document creation: resolve with the
                // payload so all files can be created in one createBatch call
                // after every storage upload finishes.
                if (shouldBatch) {
                  resolve({
                    status: "pending",
                    payload: baseDocumentData,
                    fileName: file.name
                  });
                  return;
                }

                // If mainDocument exists and has an ID, we're uploading to a bundle
                const documentResponse = mainDocument?.id
                  ? await CaseDocumentsAPI.createBundle({
                      ...baseDocumentData,
                      parent_document_id: mainDocument.id
                    })
                  : await CaseDocumentsAPI.create(baseDocumentData);

                const documentWithId: DataDocs = {
                  filePath: documentResponse.filePath,
                  docNames: documentResponse.docNames,
                  documentTitle: documentResponse.documentTitle || "",
                  uploadBy: documentResponse.uploadBy || "",
                  description: documentResponse.description || "",
                  super_class: documentResponse.super_class,
                  uploadDate: Timestamp.fromDate(
                    new Date(documentResponse.uploadDate)
                  ),
                  isDeleted: documentResponse.isDeleted,
                  id: documentResponse.id,
                  isNew: true,
                  ...(documentResponse.criterion
                    ? { criterion: documentResponse.criterion }
                    : {}),
                  ...(documentResponse.type
                    ? { type: documentResponse.type }
                    : {})
                };

                if (mainDocument !== undefined) {
                  dispatch(
                    addSubDocument({
                      data: { id: mainDocument.id!, document: documentWithId }
                    })
                  );
                }

                // Resolves the Promise with an object containing:
                // 1. status: true, indicating a successful upload.
                // 2. document: The newly added document with its Firestore ID (documentWithId).
                // 3. mainDocumentId: The main document ID or an empty string if not a sub-document.
                // This resolved value is then used in the next promise chain to generate document URLs.
                resolve({
                  status: true,
                  document: documentWithId,
                  mainDocumentId: mainDocument?.id ?? ""
                });
              } catch (error: any) {
                failedFileNames.push(file.name);
                const errorMessage =
                  error?.message || "Document creation failed";
                dispatch(
                  setFileError({ fileName: file.name, error: errorMessage })
                );
                resolve(null);
              }
            }
          );
        });
      });
      const uploadedDocuments = await Promise.all(uploadPromises);
      if (shouldBatch) {
        const pending = uploadedDocuments.filter(
          (result: any) => Boolean(result) && result.status === "pending"
        ) as { status: string; payload: any; fileName: string }[];
        if (pending.length > 0) {
          try {
            const documents = pending.map(({ payload }) => {
              // Strip individual_id from each item; it is sent once at the top
              // level of the batch request.
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { individual_id, ...item } = payload;
              return item;
            });
            const documentResponses = await CaseDocumentsAPI.createBatch({
              individual_id: uid,
              documents
            });
            documentResponses.forEach((documentResponse) => {
              dispatch(
                generateDocumentUrls({
                  documents: [
                    {
                      filePath: documentResponse.filePath,
                      id: documentResponse.id,
                      criterion: documentResponse.criterion ?? "",
                      super_class: documentResponse.super_class ?? ""
                    }
                  ],
                  mainDocId: ""
                })
              );
            });
          } catch (error: any) {
            const errorMessage = error?.message || "Document creation failed";
            pending.forEach(({ fileName }) => {
              failedFileNames.push(fileName);
              dispatch(setFileError({ fileName, error: errorMessage }));
            });
          }
        }
      } else {
        uploadedDocuments
          .filter(Boolean)
          .forEach(({ document, mainDocumentId }: any) => {
            dispatch(
              generateDocumentUrls({
                documents: [
                  {
                    filePath: document.filePath,
                    id: document.id,
                    criterion: document.criterion,
                    super_class: document.super_class
                  }
                ],
                mainDocId: mainDocumentId
              })
            );
          });
      }
      const corruptNames = Array.from(
        new Set([...corruptedFilesNames, ...failedFileNames])
      );
      if (
        corruptNames.length > 0 &&
        uploadConfiguration?.source !== "external-link"
      ) {
        dispatch(addCorruptRemovedNames(corruptNames));
      }
      dispatch(setIsLoading(false));
      dispatch(setIsUploadingDocuments(false));
      return {};
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);
